import { useEffect, type ReactNode } from "react";

import { ErrorSurface } from "@/components/error-surfaces/error-surface";
import { useFeatureFlag, useFeatureFlagPayload } from "@/hooks/feature-flags";
import { featureFlag, subscribeToFeatureFlags } from "@/lib/feature-flags";
import { formatDateTimeET } from "@/lib/format";
import type { GlobalStateStore } from "@/lib/global-state";

// The app's one **takeover** (CONTEXT.md): unlike the overlays, it *replaces*
// the rendered content — the children here are the root route's Outlet, so
// while `enable_maintenance` is on nothing of the app renders on any path,
// signed in or not.
//
// The read is the facade's synchronous contract (@/hooks/feature-flags), which
// is what makes the takeover SSR-first: the server resolves the flag from its
// own environment before the first paint, so planned downtime is the first
// thing served — no flash of the app, no loading state, and hydration agrees
// because the SSR bootstrap ships the same snapshot the HTML was rendered with.

// Only a timestamp that names its own zone. A timezone-less value parses in
// the *local* zone, which differs between the SSR pass and the browser — the
// two renders would disagree on the text.
const EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/;

/**
 * Legible when unambiguous, verbatim when not — the payload is hand-set
 * prose-adjacent config, so "Monday morning, Eastern" is as legitimate a
 * value as an ISO timestamp.
 */
function formatBackAt(backAt: string): string {
  return EXPLICIT_OFFSET.test(backAt) && !Number.isNaN(new Date(backAt).getTime())
    ? formatDateTimeET(backAt)
    : backAt;
}

/**
 * On ErrorSurface deliberately: it is the branded chrome-less shape for a page
 * "reached from outside the app as often as from inside it", which is exactly
 * this screen's situation, and one shared shape is what keeps the terminal
 * surfaces reading as one application.
 *
 * Exported for the root errorComponent's maintenance branch
 * (router-fallbacks.tsx) — the one render path the Outlet wrap below can't
 * cover, because the root errorComponent renders *instead of* the layout.
 *
 * The fallback copy covers a payload-less flip: a boolean-only local override
 * (`{"enable_maintenance": true}`) turns the flag on with no `back_at`.
 */
export function MaintenanceScreen({ backAt }: { backAt: string | undefined }) {
  const trimmed = backAt?.trim();
  return (
    <ErrorSurface
      title="We're doing some maintenance"
      message={trimmed ? `Expected back ${formatBackAt(trimmed)}` : "We'll be back shortly."}
    />
  );
}

export function MaintenanceTakeover({
  store,
  children,
}: {
  store: GlobalStateStore;
  children: ReactNode;
}) {
  const maintenance = useFeatureFlag("enable_maintenance");
  const payload = useFeatureFlagPayload("enable_maintenance");

  // Keeps the precedence store's maintenance slot (lib/global-state.ts) in
  // step with the flag, so the overlays ranked below it yield rather than
  // raising a modal over the maintenance screen. A facade *subscription*, not
  // a flag-keyed effect: the facade notifies synchronously when its snapshot
  // moves, so on a live flip the store already resolves maintenance in the
  // same batch that re-renders this component — an already-open overlay
  // <dialog> never outlives the flag by a paint. The mount-time sync covers
  // what no notification announces: the SSR bootstrap adopted before mount,
  // and the store creation's own fill (router.tsx) on a boot where the two
  // disagree.
  useEffect(() => {
    const syncMaintenanceSlot = () => {
      if (featureFlag("enable_maintenance")) store.activate("maintenance");
      else store.deactivate("maintenance");
    };
    syncMaintenanceSlot();
    return subscribeToFeatureFlags(syncMaintenanceSlot);
  }, [store]);

  if (maintenance) return <MaintenanceScreen backAt={payload?.back_at} />;
  return <>{children}</>;
}
